import { useQueries } from '@tanstack/react-query';
import { identity, noop, some, sortBy } from 'lodash-es';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getGalleryContent, getGalleryContentShared } from '../services/UploadManagementService';
import { getGallery, getGalleryParentFolder, getSharedGallery } from '../services/GalleryService';
import { CurrentFolder, Gallery, PanoramaDetails } from '../services/UploadManagementServiceTypes';
import { WaypointOptions } from './TourModeModal';
import { GalleryNavigation, useGalleryNavigation } from './useGalleryNavigation';
import { Loader } from '../common/Loader';

type ViewMode = 'edit' | 'shared' | 'read';

interface State {
  newPanoramas: PanoramaDetails[];
  currentFolder: CurrentFolder;
  currentPanorama?: PanoramaDetails;
  waypointsShown: WaypointOptions;
}

interface ProviderProps {
  viewMode: ViewMode;
  isLoading: boolean;
  isError: boolean;
  currentGallery?: Gallery;
  currentFolder: CurrentFolder;
  refetch: () => void;
}

export interface GalleryContextValue extends State, ProviderProps, GalleryNavigation {
  setState: Dispatch<SetStateAction<State>>;
  showNavigationButtons: boolean;
  goToPanorama: (index: number) => void;
}

const Context = React.createContext<GalleryContextValue>({
  isError: false,
  isLoading: false,
  newPanoramas: [],
  setState: identity,
  currentFolder: null,
  goToPanorama: noop,
  viewMode: 'shared',
  refetch: noop,
  waypointsShown: null,
  currentPanoramaIndex: 0,
  previousPanoramaIndex: 0,
  nextPanoramaIndex: 0,
  showNavigationButtons: false,
  navigablePanoramas: [],
});

export interface OpenEditorMessage {
  newPanoramas?: PanoramaDetails[];
  currentFolder?: CurrentFolder;
}

export const Provider: React.FC<ProviderProps> = ({
  refetch,
  isLoading,
  isError,
  currentGallery,
  currentFolder: loadedFolder,
  viewMode,
  children,
}) => {
  const [{ newPanoramas, currentFolder, currentPanorama, waypointsShown }, setState] =
    useState<State>({
      newPanoramas: [],
      currentFolder: null,
      currentPanorama: undefined,
      waypointsShown: null,
    });

  const navigation = useGalleryNavigation({
    waypointsShown,
    currentPanorama,
    panoramas: newPanoramas,
  });

  useEffect(() => {
    const openEditorMessage = JSON.parse(
      sessionStorage.getItem('newGallery') || '{}',
    ) as OpenEditorMessage;
    const { newPanoramas, currentFolder } = openEditorMessage;
    if (newPanoramas && currentFolder) {
      const sortedPanoramas = sortBy(newPanoramas, [(panorama) => panorama.title?.toLowerCase()]);
      setState((state) => ({
        ...state,
        newPanoramas: sortedPanoramas,
        currentFolder,
        currentPanorama: sortedPanoramas[0],
      }));
    }
  }, []);

  useEffect(() => {
    if (!currentGallery) {
      return;
    }
    setState((state) => ({
      ...state,
      newPanoramas: currentGallery.panoramas,
      currentFolder: loadedFolder,
      currentPanorama: currentGallery.panoramas[0],
      waypointsShown: currentGallery.tourMode,
    }));
  }, [currentGallery, loadedFolder, setState]);

  const goToPanorama = useCallback(
    (index: number) => {
      setState((prevState) => {
        const { newPanoramas } = prevState;
        return {
          ...prevState,
          currentPanorama: newPanoramas[index],
        };
      });
    },
    [setState],
  );
  return (
    <Context.Provider
      value={{
        refetch,
        isLoading,
        isError,
        currentGallery,
        newPanoramas,
        currentFolder,
        setState,
        goToPanorama,
        currentPanorama,
        viewMode,
        waypointsShown,
        ...navigation,
      }}
      children={children}
    />
  );
};

type EditorViews = 'edit' | 'read';

export const GalleryEditorContext: React.FC<{ galleryId?: string; viewMode: EditorViews }> = ({
  children,
  galleryId = '',
  viewMode,
}) => {
  const enabled = Boolean(galleryId);
  const [readyToShow, setReadyToShow] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const queryResults = useQueries({
    queries: [
      {
        queryKey: ['getGallery', galleryId],
        queryFn: () => getGallery({ guid: galleryId }),
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['getParentFolder', galleryId],
        queryFn: () => getGalleryParentFolder(galleryId),
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['getGalleryContent', galleryId],
        queryFn: () => getGalleryContent(galleryId),
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
      },
    ],
  });

  const galleryContent = queryResults[2].data;
  const queryIsError = some(queryResults, { isError: true });
  const isLoading = some(queryResults, { isFetching: true });

  useEffect(() => {
    if (!isLoading) {
      if (galleryContent && galleryContent.chaosCollaborationUrl) {
        window.location.href = galleryContent.chaosCollaborationUrl; // Redirect to chaos collaboration
        setIsRedirecting(true);
      } else {
        setReadyToShow(true); // Only show anything if we don't forward to collaboration. Otherwise we will render a 404 page just for a short moment and then get redirected.
      }
    }
  }, [galleryContent, isLoading]);

  const refetch = () => {
    queryResults.forEach((q) => q.refetch());
  };

  const isError = queryIsError && !isRedirecting;

  if (!readyToShow) {
    return (
      <div aria-busy='true' aria-label='Loading content'>
        <Loader />
      </div>
    );
  }

  return (
    <Provider
      viewMode={viewMode}
      isError={isError}
      isLoading={isLoading}
      currentGallery={queryResults[0].data}
      refetch={refetch}
      currentFolder={queryResults[1].data ?? null}
    >
      {children}
    </Provider>
  );
};

export const GallerySharedContext: React.FC<{ shareGuid: string; contentGuid: string }> = ({
  children,
  shareGuid,
  contentGuid,
}) => {
  const [readyToShow, setReadyToShow] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const queryResults = useQueries({
    queries: [
      {
        queryKey: ['getGalleryShared', shareGuid, contentGuid],
        queryFn: () => getSharedGallery({ shareGuid, contentGuid }),
        enabled: !!shareGuid && !!contentGuid,
        retry: false,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['getGalleryContentShared', shareGuid, contentGuid],
        queryFn: () => getGalleryContentShared(shareGuid, contentGuid),
        enabled: !!shareGuid && !!contentGuid,
        retry: false,
        refetchOnWindowFocus: false,
      },
    ],
  });

  const galleryContent = queryResults[1].data;
  const queryIsError = some(queryResults, { isError: true });
  const isLoading = some(queryResults, { isFetching: true });

  useEffect(() => {
    if (!isLoading) {
      if (galleryContent && galleryContent.chaosCollaborationUrl) {
        window.location.href = galleryContent.chaosCollaborationUrl; // Redirect to chaos collaboration
        setIsRedirecting(true);
      } else {
        setReadyToShow(true); // Only show anything if we don't forward to collaboration. Otherwise we will render a 404 page just for a short moment and then get redirected.
      }
    }
  }, [galleryContent, isLoading]);

  const refetch = () => {
    queryResults.forEach((q) => q.refetch());
  };

  const isError = queryIsError && !isRedirecting;

  if (!readyToShow) {
    return (
      <div aria-busy='true' aria-label='Loading content'>
        <Loader />
      </div>
    );
  }

  return (
    <Provider
      viewMode='shared'
      isError={isError}
      isLoading={isLoading}
      currentGallery={queryResults[0].data}
      refetch={refetch}
      currentFolder={null}
    >
      {children}
    </Provider>
  );
};

export const useGalleryContext = () => {
  return useContext(Context);
};
